import * as THREE from 'three';
import { Pass, FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass';

interface WatermarkConfig {
  name: string;
  email: string;
  client?: string;
  color: string;
  opacity: number;
  repeatScale: number;
  angle: number;
  fontFamily: string;
  fontWeight: string;
  fontSize: number;
  lineSpacing: number;
}

const DEFAULT_CONFIG: WatermarkConfig = {
  name: 'WhiteHelmet Demo',
  email: 'demo@example.com',
  client: '',
  color: '255,255,255',
  opacity: 0.5,
  repeatScale: 4,
  angle: 0.5,
  fontFamily: 'Inter, Arial, sans-serif',
  fontWeight: '200',
  fontSize: 25,
  lineSpacing: 25,
};

export class WatermarkPass extends Pass {
  public enabled = true;
  public needsSwap = true;
  public clear = false;
  public renderToScreen = false;
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly texture: THREE.CanvasTexture;
  private readonly uniforms: {
    tDiffuse: { value: THREE.Texture | null };
    watermarkTexture: { value: THREE.Texture };
    opacity: { value: number };
    angle: { value: number };
    repeat: { value: THREE.Vector2 };
    aspect: { value: number };
  };
  private readonly material: THREE.ShaderMaterial;
  private readonly quad: FullScreenQuad;
  private config: WatermarkConfig;
  private readonly viewport = new THREE.Vector4();

  constructor(config?: Partial<WatermarkConfig>) {
    super();
    this.canvas = document.createElement('canvas');
    this.canvas.width = 512;
    this.canvas.height = 512;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to create watermark canvas context');
    }
    this.context = ctx;
    this.config = { ...DEFAULT_CONFIG, ...(config ?? {}) };
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.wrapS = THREE.RepeatWrapping;
    this.texture.wrapT = THREE.RepeatWrapping;
    this.texture.anisotropy = 4;
    this.texture.encoding = THREE.sRGBEncoding;
    this.uniforms = {
      tDiffuse: { value: null },
      watermarkTexture: { value: this.texture },
      opacity: { value: this.config.opacity },
      angle: { value: this.config.angle },
      repeat: { value: new THREE.Vector2(this.config.repeatScale, this.config.repeatScale) },
      aspect: { value: 1 },
    };
    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform sampler2D watermarkTexture;
        uniform float opacity;
        uniform float angle;
        uniform vec2 repeat;
        uniform float aspect;
        varying vec2 vUv;
        void main() {
          vec2 centered = vUv - 0.5;
          centered.x *= aspect;
          float s = sin(angle);
          float c = cos(angle);
          mat2 rotation = mat2(c, -s, s, c);
          vec2 rotated = rotation * centered;
          rotated.x /= aspect;
          vec2 tiledUv = rotated * repeat + 0.5;
          vec4 baseColor = texture2D(tDiffuse, vUv);
          vec4 wm = texture2D(watermarkTexture, tiledUv);
          vec3 combined = mix(baseColor.rgb, wm.rgb, wm.a * opacity);
          gl_FragColor = vec4(combined, baseColor.a);
        }
      `,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    });
    this.quad = new FullScreenQuad(this.material);
    this.updateTexture();
  }

  setEnabled(isEnabled: boolean): void {
    this.enabled = isEnabled;
  }

  setOpacity(value: number): void {
    this.config.opacity = value;
    this.uniforms.opacity.value = value;
  }

  setRepeatScale(value: number): void {
    this.config.repeatScale = value;
    this.uniforms.repeat.value.set(value, value);
  }

  setAngle(radians: number): void {
    this.config.angle = radians;
    this.uniforms.angle.value = radians;
  }

  setColor(rgb: string): void {
    this.config.color = rgb;
    this.updateTexture();
  }

  setText(name: string, email: string, client?: string): void {
    this.config.name = name || DEFAULT_CONFIG.name;
    this.config.email = email || DEFAULT_CONFIG.email;
    this.config.client = client || '';
    this.updateTexture();
  }

  private updateTexture(): void {
    const { width, height } = this.canvas;
    this.context.setTransform(1, 0, 0, 1, 0, 0);
    this.context.clearRect(0, 0, width, height);
    this.context.globalCompositeOperation = 'source-over';
    this.context.globalAlpha = 0;
    this.context.fillStyle = 'rgba(0,0,0,0)';
    this.context.fillRect(0, 0, width, height);
    this.context.globalAlpha = 1;
    this.context.translate(width / 2, height / 2);
    this.context.rotate(-this.config.angle);
    this.context.font = `${this.config.fontWeight} ${this.config.fontSize}px ${this.config.fontFamily}`;
    this.context.textAlign = 'center';
    this.context.textBaseline = 'middle';
    this.context.fillStyle = `rgba(${this.config.color}, 1)`;
    const lines = [this.config.name, this.config.email]
      .concat(this.config.client ? [this.config.client] : [])
      .filter((value) => !!value);
    const totalLines = lines.length || 1;
    lines.forEach((line, index) => {
      const offset = (index - (totalLines - 1) / 2) * this.config.lineSpacing;
      this.context.fillText(line, 0, offset);
    });
    this.context.rotate(this.config.angle);
    this.context.translate(-width / 2, -height / 2);
    this.texture.needsUpdate = true;
  }

  render(
    renderer: THREE.WebGLRenderer,
    writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    const viewport = renderer.getViewport(this.viewport);
    const height = viewport.w === 0 ? 1 : viewport.w;
    this.uniforms.aspect.value = viewport.z / height;
    this.uniforms.tDiffuse.value = readBuffer.texture;
    if (this.renderToScreen) {
      renderer.setRenderTarget(null);
      this.quad.render(renderer);
    } else {
      renderer.setRenderTarget(writeBuffer);
      if (this.clear) {
        renderer.clear();
      }
      this.quad.render(renderer);
    }
  }

  dispose(): void {
    this.quad.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}

